import React, { useEffect, useState } from "react";
import Logo from "../../assets/images/logo-white.png";
import LogoUltimate from "../../assets/images/logo-bjb-ultimate-white.png";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import Navlink from "./Navlink";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { setLocale } from "../../redux/Slice";
import BaseUrl from "../../config/constant";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const navbarData = t("navbar", { returnObjects: true });
  const [navbar, setNavbar] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLocales, setShowLocales] = useState(false);
  const [showChild, setShowChild] = useState(false);
  const [loading, setLoading] = useState(false);
  const jwtToken = localStorage.getItem("jwt-token");
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const visibleNavbarData = navbarData.filter(
    (value) => value.isHide === false,
  );
  const registerLink = visibleNavbarData.find(
    (value) => value.href === "/login",
  );
  const primaryNavbarData = visibleNavbarData
    .filter((value) => value.href !== "/login")
    .map((value) => ({
      ...value,
      active:
        pathname === value.href ||
        value.child?.some((child) => pathname.startsWith(child.href)),
      child: value.child?.map((child) => ({
        ...child,
        active: pathname === child.href,
      })),
    }));
  const onChangeLocale = (lang) => {
    i18n.changeLanguage(lang);
    dispatch(setLocale(lang));
    setShowLocales(false);
  };

  const handleLogout = async () => {
    setLoading(true);
    const jwt = JSON.parse(jwtToken);

    await axios
      .post(`${BaseUrl}/logout`, {
        data: {
          refresh_token: jwt.refresh_token,
        },
      })
      .then((response) => {
        localStorage.removeItem("jwt-token");
        setLoading(false);
        navigate("/login");
      })
      .catch((error) => {
        console.info("error", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 0;
      setIsScrolled(scrolled);
    };

    window.addEventListener("scroll", handleScroll);

    // Membersihkan event listener saat komponen dibongkar
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <nav
      className={`w-full fixed z-40 top-0 transition-all duration-500 ease-in-out text-white ${
        isScrolled || navbar
          ? "bg-black/40 backdrop-blur-lg"
          : "bg-transparent "
      }`}
    >
      <div className="container mx-auto px-4 w-full grid grid-cols-[auto_1fr_auto] items-center lg:w-full lg:flex md:items-center">
        <div className="col-start-1 row-start-1">
          <div className="flex items-center justify-start gap-4 py-3 md:py-5 lg:block">
            <div className="lg:hidden">
              <button
                className={`p-2 ${
                  isScrolled ? "text-white" : "text-brand-cream"
                } rounded-md outline-none focus:border-gray-400 focus:border`}
                onClick={() => setNavbar(!navbar)}
                aria-expanded={navbar}
                aria-label={navbar ? "Close navigation" : "Open navigation"}
              >
                {navbar ? <XMarkIcon width={24} /> : <Bars3Icon width={24} />}
              </button>
            </div>
            <a href="/" className="items-center hidden lg:flex">
              <img src={Logo} alt="" className="w-24 2xl:w-52 md:w-40" />
            </a>
          </div>
        </div>
        <div className="col-span-3 row-start-2 lg:col-auto lg:row-auto lg:flex-1">
          <div
            className={`gap-10 items-center justify-center w-full text-center lg:w-full lg:text-left pb-10 mt-8 lg:pb-0 lg:mt-0 2xl:text-base text-sm ${
              navbar ? "block" : "hidden"
            } lg:flex`}
          >
            <ul
              className={`items-center justify-center space-y-6 ${
                isScrolled ? "text-white" : "text-brand-cream"
              } lg:flex lg:space-x-14 lg:space-y-0 font-redhat antialiased`}
            >
              {primaryNavbarData.map((value, index) => (
                <div key={value.label} className="relative">
                  <Navlink
                    label={value.label}
                    href={value.href}
                    isButton={value.isButton || false}
                    child={value.child}
                    isOpen={showChild === index}
                    isActive={value.active}
                    onParentClick={() =>
                      setShowChild(showChild === index ? null : index)
                    }
                  />
                </div>
              ))}
              {jwtToken && (
                <div className="relative">
                  <a href="/profile">Profile</a>
                </div>
              )}
            </ul>
            <div className="flex items-center justify-center gap-1 mt-4 lg:mt-0 text-xs md:gap-2 md:text-sm font-ttFirsNeue">
              {registerLink && !jwtToken ? (
                <a
                  href={registerLink.href}
                  className={`whitespace-nowrap ${
                    registerLink.isButton
                      ? "rounded-md border border-neutral-600 bg-[#F79838] text-white font-bold px-2 py-1.5 backdrop-blur-xl md:px-3 md:py-2"
                      : ""
                  }`}
                >
                  {registerLink.label}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="whitespace-nowrap rounded-md border border-neutral-600 bg-red-600 text-white font-bold px-2 py-1.5 backdrop-blur-xl md:px-3 md:py-2"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-current" />
                    </div>
                  ) : (
                    "Logout"
                  )}
                </button>
              )}
              <div className="relative font-ttFirsNeue">
                <button
                  type="button"
                  className="flex items-center gap-1 rounded-md px-1 py-1.5 md:px-2 md:py-2"
                  onClick={() => setShowLocales(!showLocales)}
                  aria-expanded={showLocales}
                  aria-haspopup="true"
                  aria-label="Select language"
                >
                  {i18n.language === "id" ? "ID" : "EN"}
                  <span aria-hidden="true">▾</span>
                </button>
                {showLocales ? (
                  <ul className="absolute right-0 top-full z-10 mt-2 min-w-full rounded-md border border-neutral-600 bg-black/80 p-1 text-center backdrop-blur-lg">
                    {["en", "id"].map((lang) => (
                      <li key={lang}>
                        <button
                          type="button"
                          onClick={() => onChangeLocale(lang)}
                          className={`w-full rounded px-3 py-2 ${
                            i18n.language === lang ? "text-[#ffaf00]" : ""
                          }`}
                        >
                          {lang.toUpperCase()}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        {/* <div className="col-start-2 row-start-1 justify-self-center lg:col-auto lg:row-auto">
          <div className="flex items-center gap-1 text-xs md:gap-2 md:text-sm">
            {registerLink ? (
              <a
                href={registerLink.href}
                className={`whitespace-nowrap ${
                  registerLink.isButton
                    ? "rounded-md border border-neutral-600 bg-[#F79838] text-white font-bold px-2 py-1.5 backdrop-blur-xl md:px-3 md:py-2"
                    : ""
                }`}
              >
                {registerLink.label}
              </a>
            ) : null}
            <div className="relative">
              <button
                type="button"
                className="flex items-center gap-1 rounded-md px-1 py-1.5 md:px-2 md:py-2"
                onClick={() => setShowLocales(!showLocales)}
                aria-expanded={showLocales}
                aria-haspopup="true"
                aria-label="Select language"
              >
                {i18n.language === "id" ? "ID" : "EN"}
                <span aria-hidden="true">▾</span>
              </button>
              {showLocales ? (
                <ul className="absolute right-0 top-full z-10 mt-2 min-w-full rounded-md border border-neutral-600 bg-black/80 p-1 text-center backdrop-blur-lg">
                  {["en", "id"].map((lang) => (
                    <li key={lang}>
                      <button
                        type="button"
                        onClick={() => onChangeLocale(lang)}
                        className={`w-full rounded px-3 py-2 ${
                          i18n.language === lang ? "text-[#ffaf00]" : ""
                        }`}
                      >
                        {lang.toUpperCase()}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </div> */}
        <div className="col-start-3 row-start-1 justify-self-end mr-2">
          <div className="flex items-center justify-between py-3 md:py-5 lg:block">
            <a href="/" className="items-center flex lg:hidden">
              <img src={Logo} alt="" className="w-24 2xl:w-52 md:w-40" />
            </a>
            <a href="/" className="flex items-center">
              <img
                src={LogoUltimate}
                alt=""
                className="w-24 2xl:w-52 md:w-40"
              />
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}
